import React, { useCallback, useState } from 'react';
import { ClassNames, type CSSObject } from '@emotion/react';
import { Resizable } from 're-resizable';
import { View } from '@amzn/stencil-react-components/layout';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';
import {
  BOTTOM_ANCHOR_FRACTION,
  MIN_HEIGHT,
  MIN_WIDTH,
  TOP_CHROME_PX,
  defaultNormalSize,
  maximizedSize,
  type PanelSize,
} from './astraPanelSizing';

/** Test id on the resizable frame element. */
export const ASTRA_PANEL_FRAME_TEST_ID = 'astra-panel-frame';
/** Accessible name of the resize grip. */
export const ASTRA_RESIZE_GRIP_LABEL = 'Resize Astra';

export interface AstraPanelSizeState {
  /** Expanded to the maximized footprint. */
  readonly isMaximized: boolean;
  /** Flip between the normal and maximized footprints. */
  readonly toggleMaximize: () => void;
  /** Props to spread onto `AstraPanelFrame`. */
  readonly frameProps: Pick<
    AstraPanelFrameProps,
    'isMaximized' | 'normalSize' | 'onNormalSizeChange'
  >;
}

/**
 * Owns the floating panel's size state: the maximize mode and the user's last
 * drag size (kept so restoring from maximized returns to it). A manual drag
 * always lands the panel back in normal mode.
 */
export function useAstraPanelSize(): AstraPanelSizeState {
  const [isMaximized, setIsMaximized] = useState(false);
  const [normalSize, setNormalSize] = useState<PanelSize>(defaultNormalSize);

  const toggleMaximize = useCallback((): void => {
    setIsMaximized((value) => !value);
  }, []);

  const onNormalSizeChange = useCallback((size: PanelSize): void => {
    setIsMaximized(false);
    setNormalSize(size);
  }, []);

  return {
    isMaximized,
    toggleMaximize,
    frameProps: { isMaximized, normalSize, onNormalSizeChange },
  };
}

export interface AstraPanelFrameProps {
  readonly isMaximized: boolean;
  /** The user's drag size, used whenever the panel is not maximized. */
  readonly normalSize: PanelSize;
  /** Fired when the user finishes a drag with the panel's new footprint. */
  readonly onNormalSizeChange: (size: PanelSize) => void;
  /** Accessible name of the floating dialog the frame's surface exposes. */
  readonly ariaLabel: string;
  /** Visible card rendered inside the frame. */
  readonly children: React.ReactNode;
}

/**
 * Bottom-right anchor and viewport caps. Size itself is controlled by
 * re-resizable; these pure-CSS caps keep a resized panel clear of the top
 * chrome and within the viewport. The anchor offsets and the chrome height
 * are structural values with no design token.
 */
const FRAME_STYLE: CSSObject = {
  position: 'fixed',
  bottom: `${Math.round((1 - BOTTOM_ANCHOR_FRACTION) * 100)}%`,
  right: '2%',
  maxHeight: `max(${MIN_HEIGHT}px, calc(${BOTTOM_ANCHOR_FRACTION * 100}vh - ${TOP_CHROME_PX}px))`,
  maxWidth: '96vw',
  zIndex: token('dimensions.z-index.900'),
  display: 'flex',
  flexDirection: 'column',
};

/**
 * Card surface inside the frame: fills it, clips the panel's own scroll
 * region, carries the elevation and radius of a raised Stencil surface, and
 * is the `dialog` the floating panel is announced as.
 */
const SURFACE_STYLE: CSSObject = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  backgroundColor: token('color.surface.bg-default'),
  borderRadius: token('dimensions.border.radius.300'),
  boxShadow: token('shadow.300'),
};

/**
 * re-resizable writes `position: relative` inline on its root, which would beat
 * the emotion class; the override has to be inline too. The anchor and caps
 * stay in FRAME_STYLE.
 */
const FRAME_INLINE_STYLE: React.CSSProperties = { position: 'fixed' };

/**
 * re-resizable sets the handle's position/size/cursor inline; this only adds
 * stacking above the card and disables native touch scrolling so the drag
 * works on touch devices.
 */
const HANDLE_STYLE: React.CSSProperties = {
  zIndex: token('dimensions.z-index.100'),
  touchAction: 'none',
};

/** Grip rendered inside the top-left handle (fills the library's hit area). */
const GRIP_STYLE: CSSObject = {
  display: 'block',
  width: '100%',
  height: '100%',
  cursor: 'nwse-resize',
};

/**
 * The bottom-right fixed, drag-resizable frame the desktop Astra panel lives
 * in. Position, size caps and the resize grip are all here; the body is
 * whatever the caller renders inside.
 */
export function AstraPanelFrame({
  isMaximized,
  normalSize,
  onNormalSizeChange,
  ariaLabel,
  children,
}: AstraPanelFrameProps): React.JSX.Element {
  const handleResizeStop = useCallback(
    (_event: unknown, _direction: unknown, ref: HTMLElement): void => {
      onNormalSizeChange({ width: ref.offsetWidth, height: ref.offsetHeight });
    },
    [onNormalSizeChange],
  );

  // The drag floor only applies while the user can drag (not maximized); the
  // props are omitted rather than passed as `undefined` for exactOptionalPropertyTypes.
  const dragLimits = isMaximized ? {} : { minWidth: MIN_WIDTH, minHeight: MIN_HEIGHT };

  // `ClassNames` hands re-resizable (a third-party class component that only
  // takes `className`) an emotion class for FRAME_STYLE; the `css` prop is
  // only wired up for Stencil components in this app.
  return (
    <ClassNames>
      {(emotion) => (
        <Resizable
          className={emotion.css(FRAME_STYLE)}
          style={FRAME_INLINE_STYLE}
          data-testid={ASTRA_PANEL_FRAME_TEST_ID}
          size={isMaximized ? maximizedSize() : normalSize}
          {...dragLimits}
          // No bounds="window": it assumes a top-left anchor and would clamp this
          // bottom-right panel's growth; the CSS caps size instead. Resize from the
          // top/left edges and the top-left corner — the edges away from the anchor.
          enable={isMaximized ? false : { top: true, left: true, topLeft: true }}
          handleStyles={{ top: HANDLE_STYLE, left: HANDLE_STYLE, topLeft: HANDLE_STYLE }}
          handleComponent={{
            topLeft: (
              <View
                css={GRIP_STYLE}
                role="separator"
                aria-label={ASTRA_RESIZE_GRIP_LABEL}
              />
            ),
          }}
          onResizeStop={handleResizeStop}
        >
          <View css={SURFACE_STYLE} role="dialog" aria-label={ariaLabel}>
            {children}
          </View>
        </Resizable>
      )}
    </ClassNames>
  );
}
